"use client";

import { useEffect } from "react";
import { isPublicSandboxAuthEnabled } from "@/lib/auth/sandbox";
import { applyDatadogUserIdentity } from "@/lib/observability/apply-datadog-user";
import { createDatadogIdentityLoadGate } from "@/lib/observability/datadog-identity-load-gate";
import { resolveDatadogUserIdentity } from "@/lib/observability/datadog-user";
import { useSupabaseClient } from "@/lib/supabase/client";

export function DatadogUserContext() {
  const supabase = useSupabaseClient();

  useEffect(() => {
    if (isPublicSandboxAuthEnabled()) {
      return;
    }

    let isMounted = true;
    const loadGate = createDatadogIdentityLoadGate();

    async function loadIdentity() {
      const loadToken = loadGate.begin();
      const [
        { data: claimsData },
        {
          data: { user },
        },
      ] = await Promise.all([supabase.auth.getClaims(), supabase.auth.getUser()]);

      if (!isMounted || !loadGate.isCurrent(loadToken)) return;

      applyDatadogUserIdentity(
        resolveDatadogUserIdentity({
          user,
          claims: claimsData?.claims,
        })
      );
    }

    loadIdentity().catch(() => {
      // Keep the identity applied at RUM init if the session refresh fails.
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        loadGate.invalidate();
        applyDatadogUserIdentity(null);
        return;
      }

      void loadIdentity();
    });

    return () => {
      isMounted = false;
      loadGate.invalidate();
      subscription.unsubscribe();
    };
  }, [supabase]);

  return null;
}
